import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ICandles, IStory } from "pages/People/people.types";
import { useLocation, useParams } from "react-router-dom";
import { getCandles, getStory, getTributes } from "api/stories";
import { useAppSelector } from "../store";
import { ITributes } from "components/organisms/Tributes/tributes.types";
import { ICandle } from "components/organisms/StoryFace/storyFace.types";

type StoryDataContextType = {
  story: IStory | null;
  setStory: (v: IStory) => void;
  candles: ICandles | null;
  addCandle: (c: ICandle) => void;
  tributes: ITributes[];
  storyLoading: boolean;
  isCreator: boolean;
  setTributes: React.Dispatch<React.SetStateAction<ITributes[]>>;
};

const StoryDataContext = createContext<StoryDataContextType | undefined>(
  undefined
);

export const StoryDataProvider = ({children}: {
  children: React.ReactNode;
}) => {
  const { profile } = useAppSelector((state) => state.profile);
  const { id } = useParams();
  const location = useLocation();

  const [story, setStory] = useState<IStory | null>(null);
  const [candles, setCandles] = useState<ICandles | null>(null);
  const [tributes, setTributes] = useState<ITributes[]>([]);

  const [storyLoading, setStoryLoading] = useState(true);

  const isMyStory = location.pathname.includes('/my-stories');
  const isCreator = profile ? profile?.id === story?.creator : false;

  const loadStory = async (signal: AbortSignal) => {
    if (!id) {
      setStory(null);
      setCandles(null);
      setTributes([]);
      setStoryLoading(false);
      return;
    }

    setStoryLoading(true);

    try {
      const [storyRes, candlesRes, tributesRes] = await Promise.all([
        getStory(id, isMyStory, { signal }),
        getCandles(id, { signal }),
        getTributes(id, { signal })
      ]);

      setStory(storyRes);
      setCandles(candlesRes);
      setTributes(tributesRes);
    } catch (e: any) {
      if (e.name === "CanceledError") {
        console.log("Request aborted");
        return;
      }
      console.error(e);
    } finally {
      if (!signal.aborted) {
        setStoryLoading(false);
      }
    }
  };

  const addCandle = (candle: ICandle) => {
    setCandles(p => {
      if (p?.results) return {
        count: p.count + 1,
        results: [...p.results, candle]
      }
      return null
    });
  }

  useEffect(() => {
    const controller = new AbortController();
    loadStory(controller.signal);

    return () => {
      controller.abort();
    };
  }, [id]);

  return (
    <StoryDataContext.Provider value={{
      story,
      setStory,
      candles,
      tributes,
      setTributes,
      addCandle,
      storyLoading,
      isCreator,
    }}>
      {children}
    </StoryDataContext.Provider>
  );
};

export const useStoryDataContext = () => {
  const context = useContext(StoryDataContext);
  if (!context) {
    throw new Error("useStoryContext must be used within a StoryProvider");
  }
  return context;
};
